import { axiosInstanceWebsite, axiosInstanceAnalytics } from "./instance";
import { AnalyticsFunnelPayload, AxiosResponse, FunnelPayload, MultiFunnelPayload } from "./interface";

const SECTIONS_GENERATED_CHUNK_SIZE = 75;

export interface SectionsGeneratedResponseItem {
    done: boolean;
    website_id: string;
    has_errors?: boolean;
    error_sections?: string[];
}

const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
};

/**
 * @deprecated Legacy single-funnel creation endpoint.
 * Active onboarding uses `postBulkFunnels` so product creation, templates,
 * promotions, and async generation share one contract.
 */
export const postFunnel = async (payload: FunnelPayload) => {
    const response = await axiosInstanceWebsite.post('/websites', payload);
    return response.data;
};

export interface BulkFunnelResponseItem {
    id: string;
    owner_id?: string;
    brand_name?: string;
    sale_angle?: { name: string; description: string };
    status?: string;
    [key: string]: any;
}

export const postBulkFunnels = async (payload: MultiFunnelPayload): Promise<BulkFunnelResponseItem[]> => {
    const response = await axiosInstanceWebsite.post('/websites/bulk', payload);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
};

export const duplicateFunnel = async (websiteId: string) => {
    const response = await axiosInstanceWebsite.post(`/websites/${websiteId}/clone`);
    return response.data;
};

export const getAnalyticsFunnel = async (data: AnalyticsFunnelPayload) => {
    const response = await axiosInstanceAnalytics.post(`/dashboard-analytics`, data);
    return response.data;
};

export const getSectionFunnel = async (websiteId: string, sectionId: string) => {
    const response = await axiosInstanceWebsite.get(`/websites/${websiteId}/sections/${sectionId}`);
    return response.data;
};

export const retrySection = async (websiteId: string, sectionName: string) => {
    const response = await axiosInstanceWebsite.post(
        `/websites/${websiteId}/sections/${sectionName}/retry`
    );
    return response.data;
};

const getSectionsGeneratedChunk = async (websiteIds: string[]): Promise<SectionsGeneratedResponseItem[]> => {
    const response: AxiosResponse = await axiosInstanceWebsite.get(`/websites/sections/generated`, {
        params: {
            website_ids: websiteIds.join(","),
        },
    });
    return Array.isArray(response.data) ? response.data : [];
};

export const getSectionsGenerated = async (data: string[]): Promise<SectionsGeneratedResponseItem[]> => {
    const websiteIds = Array.from(new Set(data.filter(Boolean)));
    if (websiteIds.length === 0) return [];

    const chunks = chunkArray(websiteIds, SECTIONS_GENERATED_CHUNK_SIZE);
    const results = await Promise.allSettled(
        chunks.map((chunk) => getSectionsGeneratedChunk(chunk))
    );

    const fulfilled = results
        .filter((result): result is PromiseFulfilledResult<SectionsGeneratedResponseItem[]> => result.status === "fulfilled")
        .flatMap((result) => result.value);

    if (fulfilled.length > 0) return fulfilled;

    const firstError = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
    throw firstError?.reason ?? new Error("No pudimos consultar el estado de generación de los embudos.");
};

export const getFunnelEventsAnalytics = async (data: AnalyticsFunnelPayload) => {
    const response = await axiosInstanceAnalytics.post(`/funnel-events-analytics`, data);
    return response.data;
};

export const getAbandonedCarts = async (data: AnalyticsFunnelPayload) => {
    const response = await axiosInstanceAnalytics.post(`/abandoned-carts`, data);
    return response.data;
};

// Section Templates
export interface SectionTemplate {
    id: number;
    name: string;
    type: string;
    family_key?: string | null;
    section_type: string;
    url: string | null;
    metadata: Record<string, any> | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export const getSectionTemplates = async (type_param?: string, value_params?: string): Promise<SectionTemplate[]> => {
    const params = type_param ? `${type_param}=${value_params}` : '';
    const response = await axiosInstanceWebsite.get(`/websites/section-templates?${params}`);
    return response.data;
};

export const getSectionTemplatesByType = async (type: string): Promise<SectionTemplate[]> => {
    const response = await axiosInstanceWebsite.get(`/websites/section-templates/type/${type}`);
    return response.data;
};

export interface OnboardingCardBinding {
    id: number;
    family_key: string;
    binding_key: string;
    style_id: string;
    palette_fallback: string;
    display_name: string;
    description: string | null;
    preview_url: string;
    thumbnail_url: string;
    sort_order: number;
    is_published_for_users?: boolean;
    is_active?: boolean;
    contract_version: string;
    kind: 'html' | 'image' | 'element';
}

export const getOnboardingCards = async (): Promise<OnboardingCardBinding[]> => {
    const response = await axiosInstanceWebsite.get('/websites/template-studio/onboarding-cards');
    return response.data;
};
